import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { isDarkTheme, getAgentImageBaseFilter } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import useFavoriteAgent from '@/hooks/useFavoriteAgent';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import AgentOwnerMenu from '@/components/Agents/AgentOwnerMenu';
import { getBotPresentation } from '@/components/Agents/botPresentation';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const isWeb = Platform.OS === 'web';
const AVATAR = 56;
const RAIL_WIDTH = 196;

const stop = (event) => {
  event?.stopPropagation?.();
  event?.preventDefault?.();
};

function FavoriteStar({ agentId, colors }) {
  const { isSaved, toggleFavorite } = useFavoriteAgent(agentId);
  const { t } = useTranslation();
  return (
    <Tooltip label={t(isSaved ? 'pages.agents.removeFavoriteTooltip' : 'pages.agents.addFavoriteTooltip')}>
      <Pressable
        onPress={(event) => {
          stop(event);
          toggleFavorite();
        }}
        onPressIn={stop}
        accessibilityRole="button"
        accessibilityState={{ selected: isSaved }}
        accessibilityLabel={isSaved ? t('nav.routes.agentChat.unsave') : t('pages.agents.colFavorite')}
        style={({ hovered, pressed }) => [
          {
            width: 40,
            height: 40,
            borderRadius: 9,
            alignItems: 'center',
            justifyContent: 'center',
          },
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
        ]}
      >
        <Icon
          name="star"
          solid={isSaved}
          size={15}
          color={isSaved ? colors.text_color_primary : colors.text_color_tertiary}
        />
      </Pressable>
    </Tooltip>
  );
}

function AgentCard({
  agent,
  onPress,
  onUseAgent,
  isLoading,
  width: explicitWidth,
  fillHeight = true,
  compact = false,
  variant = 'default',
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const startAgentWorkChat = useStartAgentWorkChat();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isMenuOpenRef = useRef(false);
  const menuClosedAtRef = useRef(0);
  const isRail = variant === 'rail';
  const styles = useMemo(
    () => createStyles(colors, getCatalogPalette(theme), explicitWidth, fillHeight, isRail, compact),
    [colors, theme, explicitWidth, fillHeight, isRail, compact],
  );
  const presentation = getBotPresentation(agent, isDarkTheme(theme));
  const imageFilter = getAgentImageBaseFilter(theme, !!agent?.flags?.isImageInvertable);
  const filteredStyle = agent?.flags?.isImageInvertable && imageFilter ? { filter: imageFilter } : null;

  const handleMenuStateChange = useCallback((open) => {
    isMenuOpenRef.current = open;
    if (!open) menuClosedAtRef.current = Date.now();
  }, []);

  const handleOpenChat = useCallback(() => {
    if (isMenuOpenRef.current || Date.now() - menuClosedAtRef.current < 350) return;
    if (onUseAgent) onUseAgent(agent);
    else if (onPress) onPress(agent);
    else startAgentWorkChat(agent, { source: 'agent_card_use_agent', chooseMode: true });
  }, [onUseAgent, onPress, agent, startAgentWorkChat]);

  if (isLoading) {
    return (
      <View style={styles.card} aria-busy={true}>
        <View style={styles.topRow}>
          <View style={[styles.skeleton, styles.avatar]} />
        </View>
        <View style={styles.identity}>
          <View style={[styles.skeleton, { height: 22, width: '75%' }]} />
          <View style={[styles.skeleton, { height: 18, width: '90%' }]} />
        </View>
      </View>
    );
  }
  if (!agent) return null;

  const useBotLabel = t('pages.agents.openBot');

  return (
    <View style={[styles.card, hovered && styles.cardHovered, focused && styles.cardFocused]}>
      <Pressable
        onPress={handleOpenChat}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        accessibilityRole="button"
        accessibilityLabel={presentation.name}
        accessibilityHint={useBotLabel}
        style={[StyleSheet.absoluteFillObject, { borderRadius: 20 }]}
      />
      <View style={styles.topRow} pointerEvents="box-none">
        <View style={[styles.avatar, { backgroundColor: presentation.useAvatar ? 'transparent' : presentation.background }, presentation.useAvatar && isWeb && filteredStyle]} pointerEvents="none">
          {presentation.useAvatar ? (
            <Image accessibilityLabel="" source={{ uri: agent.avatarPath }} style={[styles.avatarImage, !isWeb && filteredStyle]} contentFit="cover" />
          ) : (
            <Icon name={presentation.icon} size={25} color={presentation.foreground} />
          )}
        </View>
      </View>
      <View style={styles.identity} pointerEvents="none">
        <Text weight="bold" color="primary" numberOfLines={2} style={styles.title}>
          {presentation.name}
        </Text>
        {!!agent.flags?.isPlusOnly && <View style={styles.plusPill}><Text size="xxs" weight="bold" color="secondary">PLUS</Text></View>}
        {!!presentation.summary && <Text size="s" color="secondary" numberOfLines={2} style={styles.description}>{presentation.summary}</Text>}
      </View>
      <View style={styles.actions}>
        {!agent.isOwner && <FavoriteStar agentId={agent.id} colors={colors} />}
        <AgentOwnerMenu agent={agent} size={40} iconSize={15} onMenuStateChange={handleMenuStateChange} />
      </View>
    </View>
  );
}

const createStyles = (colors, palette, explicitWidth, fillHeight, isRail, compact) => StyleSheet.create({
  card: {
    width: explicitWidth != null ? explicitWidth : (isRail ? RAIL_WIDTH : '100%'),
    // Percentage heights expand against the native ScrollView's content,
    // pushing the action far below the card. Web grids have bounded rows.
    ...(isWeb && fillHeight && !isRail ? { alignSelf: 'stretch', height: '100%' } : {}),
    minWidth: 0,
    minHeight: compact ? 156 : isRail ? 196 : 168,
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: compact ? 16 : 18,
    gap: 14,
    borderWidth: 1,
    borderRadius: 20,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    ...(isWeb ? { transition: 'border-color 150ms ease, background-color 150ms ease' } : {}),
  },
  cardHovered: { borderColor: palette.activeBorder },
  cardFocused: { borderColor: colors.text_color_link },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 6,
  },
  avatar: {
    width: compact ? 48 : AVATAR,
    height: compact ? 48 : AVATAR,
    borderRadius: 15,
    backgroundColor: palette.preview,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImage: { width: compact ? 48 : AVATAR, height: compact ? 48 : AVATAR },
  actions: { position: 'absolute', top: compact ? 8 : 10, right: compact ? 8 : 10, flexDirection: 'row', alignItems: 'center', gap: 2 },
  identity: { flex: 1, minWidth: 0, justifyContent: 'flex-start', gap: 5 },
  title: { fontSize: compact ? 16 : 18, lineHeight: compact ? 22 : 24, letterSpacing: -0.4, textAlign: 'center' },
  plusPill: {
    alignSelf: 'center',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: palette.border,
  },
  description: { lineHeight: 19, textAlign: 'center' },
  skeleton: { backgroundColor: palette.preview, borderRadius: 8, alignSelf: 'center' },
});

AgentCard.propTypes = {
  agent: PropTypes.object,
  onPress: PropTypes.func,
  onUseAgent: PropTypes.func,
  isLoading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fillHeight: PropTypes.bool,
  compact: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'rail']),
};

export default React.memo(AgentCard);
